import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { CompressSolidV6, ExpandSolidV6 } from "../Icomoon";
import useTerraDraw from "./hooks/useTerraDraw";

type PolygonFeature = {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: [number, number][][];
  };
  properties: Record<string, unknown>;
};

export interface TerraDrawButtonProps {
  onChange: (feature: PolygonFeature | null) => void;
  defaultPolygon?: { lat: number; lng: number }[];
  onDrawingStateChange?: (isDrawing: boolean) => void;
  clearRequestKey?: number;
}

const createFeatureFromPolygon = (polygon: { lat: number; lng: number }[]): PolygonFeature => {
  const coordinates = polygon.map((point) => [point.lng, point.lat] as [number, number]);

  if (coordinates.length > 2) {
    const [firstLng, firstLat] = coordinates[0];
    const [lastLng, lastLat] = coordinates[coordinates.length - 1];

    if (firstLng !== lastLng || firstLat !== lastLat) {
      coordinates.push([firstLng, firstLat]);
    }
  }

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coordinates],
    },
    properties: { mode: "polygon" },
  };
};

const getPolygonBounds = (polygon: { lat: number; lng: number }[]): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds();
  const poly = new google.maps.Polygon({ paths: polygon });
  poly.getPaths().forEach((path) => {
    path.forEach((latlng) => {
      bounds.extend(latlng);
    });
  });
  return bounds;
};

const startTerraDraw = (terraDraw: TerraDrawButtonsInstance) => {
  if (!terraDraw.enabled) {
    terraDraw.start();
  }
};

const clearTerraDraw = (terraDraw: TerraDrawButtonsInstance) => {
  if (terraDraw.enabled) {
    terraDraw.clear();
    terraDraw.stop();
  }
};

type TerraDrawButtonsInstance = NonNullable<ReturnType<typeof useTerraDraw>>;

const TerraDrawButtons = ({
  onChange,
  defaultPolygon,
  onDrawingStateChange,
  clearRequestKey,
}: TerraDrawButtonProps) => {
  const map = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const terraDraw = useTerraDraw({ map });
  const previousClearRequestKey = useRef(clearRequestKey);
  const onChangeRef = useRef(onChange);
  const onDrawingStateChangeRef = useRef(onDrawingStateChange);
  const defaultPolygonKey = defaultPolygon?.map(({ lat, lng }) => `${lat},${lng}`).join("|") ?? "";

  onChangeRef.current = onChange;
  onDrawingStateChangeRef.current = onDrawingStateChange;

  useEffect(() => {
    if (!terraDraw) return;

    const handleFinish = (_id: string | number, { mode }: { mode: string }) => {
      if (mode !== "select") terraDraw.setMode("select");
      setIsDrawing(true);
      onDrawingStateChangeRef.current?.(true);
      const features = terraDraw.getSnapshot() as unknown as PolygonFeature[];
      onChangeRef.current(features.find((feature) => feature.geometry.type === "Polygon") ?? null);
    };

    terraDraw.on("finish", handleFinish);

    return () => {
      terraDraw.off("finish", handleFinish);
    };
  }, [terraDraw]);

  useEffect(() => {
    if (!terraDraw || !defaultPolygon?.length) return;
    if (defaultPolygon.length > 2) {
      startTerraDraw(terraDraw);
      terraDraw.clear();
      terraDraw.addFeatures([createFeatureFromPolygon(defaultPolygon)]);
      setIsDrawing(true);
      terraDraw.setMode("select");
    }

    map?.fitBounds(getPolygonBounds(defaultPolygon));
    if (defaultPolygon.length < 3) map?.setZoom(12);
  }, [terraDraw, map, defaultPolygonKey]);

  useEffect(() => {
    if (!terraDraw || clearRequestKey === undefined || clearRequestKey === previousClearRequestKey.current) {
      return;
    }

    previousClearRequestKey.current = clearRequestKey;
    clearTerraDraw(terraDraw);
    setIsDrawing(false);
  }, [terraDraw, clearRequestKey]);

  useEffect(() => {
    if (!terraDraw) return;
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);
      if (isFs) return;

      const coordinates = // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        terraDraw?.getSnapshot().find((o) => o.geometry.type === "Polygon")?.geometry.coordinates[0] as
          [number, number][] | undefined;
      if (coordinates) {
        const bounds = getPolygonBounds(coordinates.map(([lng, lat]) => ({ lat, lng })));
        map?.fitBounds(bounds);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [terraDraw]);

  return (
    <div className="tw-absolute tw-top-[10px] tw-right-[10px] tw-z-10 tw-flex tw-items-center tw-gap-[8px]">
      <button
        className="tw-bg-white tw-border-none tw-shadow-lg tw-rounded-[2px] tw-px-[12px] tw-py-[10px]"
        onClick={() => {
          if (terraDraw) {
            if (!isDrawing) {
              startTerraDraw(terraDraw);
              terraDraw.clear();
              terraDraw.setMode("polygon");
              setIsDrawing(true);
              onDrawingStateChange?.(true);
            } else {
              clearTerraDraw(terraDraw);
              setIsDrawing(false);
              onDrawingStateChange?.(false);
              onChange(null);
            }
          }
        }}
      >
        {isDrawing ? "Clear" : "Draw"}
      </button>
      <button
        className="tw-bg-white tw-border-none tw-shadow-lg tw-rounded-[2px] tw-px-[12px] tw-py-[10px]"
        onClick={() => {
          if (isFullscreen) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            document.exitFullscreen();
          } else {
            const parentNode = map?.getDiv().parentNode as HTMLElement;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            parentNode?.requestFullscreen();
          }
        }}
      >
        {isFullscreen ? (
          <CompressSolidV6 size="xl" className="tw-block" />
        ) : (
          <ExpandSolidV6 size="xl" className="tw-block" />
        )}
      </button>
    </div>
  );
};

export default TerraDrawButtons;
